/**
 * Remplacement du titulaire ou du suppléant d'un mandat CAC — calqué sur le remplacement d'un
 * administrateur (`gouvernance/.../f360-mandat-transition.config.ts`).
 *
 * - Titulaire et suppléant sont deux mandats distincts : chacun se remplace sur sa propre ligne.
 * - Le remplaçant **continue** le mandat : ni rang ni échéance ne changent, une seule écriture
 *   (PATCH `personne_physique_remplacant_id`, `date_debut_remplacement`, `motif_modification`).
 * - Un mandat échu ne se remplace pas, et le jour de son échéance il l'est déjà : même règle que le
 *   serveur (`date_fin <= aujourd'hui` → « Ce mandat est expiré. »).
 * - Le motif vient du référentiel des administrateurs (`MOTIF_MODIFICATION_MANDAT`). Le serveur
 *   ne portant qu'un texte, c'est le libellé qui est enregistré (BE-040).
 *
 * ⚠️ Un seul remplaçant par mandat : un nouveau remplacement écrase le précédent (même limite que
 * BE-031 pour les administrateurs).
 */

import type { ParameterValueSummary } from '../../../../core/models/gouvernance/referentiel.model';
import type { RattachementCac, RemplacementCac } from '../models/cac.model';

/** Longueur maximale de `motif_modification` côté serveur (`CharField(max_length=255)`). */
export const LONGUEUR_MAX_MOTIF_CAC = 255;

/** État d'un mandat au regard du remplacement, lu par les grilles comme par la modale. */
export type EtatRemplacementCac = {
    expire: boolean;
    remplacable: boolean;
    /** Un remplaçant figure déjà : le nouveau l'écrasera. N'interdit pas le remplacement. */
    dejaRemplace: boolean;
};

/** Jour local d'une date `AAAA-MM-JJ` ou `JJ-MM-AAAA`, à minuit — jamais d'heure ni de fuseau. */
export function jourLocal(valeur: string | null | undefined): Date | null {
    if (!valeur) {
        return null;
    }
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(valeur);
    const fr = /^(\d{2})-(\d{2})-(\d{4})$/.exec(valeur);
    const [annee, mois, jour] = iso ? [iso[1], iso[2], iso[3]] : fr ? [fr[3], fr[2], fr[1]] : [];
    if (!annee) {
        return null;
    }
    const date = new Date(Number(annee), Number(mois) - 1, Number(jour));
    return Number.isNaN(date.getTime()) ? null : date;
}

function debutDuJour(instant: Date): number {
    return new Date(instant.getFullYear(), instant.getMonth(), instant.getDate()).getTime();
}

/** Un mandat sans date de fin lisible reste remplaçable : le serveur demeure l'autorité. */
export function etatRemplacementCac(
    rattachement: Pick<RattachementCac, 'date_fin_mandat' | 'remplacant_nom'>,
    maintenant: Date = new Date(),
): EtatRemplacementCac {
    const fin = jourLocal(rattachement.date_fin_mandat);
    const expire = fin !== null && fin.getTime() <= debutDuJour(maintenant);
    return { expire, remplacable: !expire, dejaRemplace: !!rattachement.remplacant_nom };
}

/** Rôle dont on remplace le commissaire : le mandat porte les deux, chacun se remplace. */
export type RoleRemplaceCac = 'TITULAIRE' | 'SUPPLEANT';

/** Champs saisis dans la modale. */
export type ChampsRemplacementCac = {
    role: RoleRemplaceCac;
    remplacantId: string;
    dateDebut: Date | null;
    motifId: string;
};

/** Ce que la saisie doit respecter, tiré du rôle remplacé dans le mandat. */
export type ContexteRemplacementCac = {
    /** Personne physique qui tient le rôle — elle ne peut pas se remplacer elle-même. */
    porteurId: string;
    /** Désignation du mandat courant (`AAAA-MM-JJ` ou `JJ-MM-AAAA`). */
    dateDesignation: string | null;
    dateFin: string | null;
};

/** Bornes de la date de prise d'effet : dans la période du mandat en cours. */
export function bornesRemplacementCac(contexte: ContexteRemplacementCac): { min: Date | null; max: Date | null } {
    return { min: jourLocal(contexte.dateDesignation), max: jourLocal(contexte.dateFin) };
}

/** Erreurs de saisie, par champ. Vide, le remplacement peut être soumis. */
export function erreursRemplacementCac(champs: ChampsRemplacementCac, contexte: ContexteRemplacementCac): Record<string, string> {
    const erreurs: Record<string, string> = {};
    if (!champs.remplacantId) {
        erreurs['remplacant'] = 'Le commissaire aux comptes remplaçant est obligatoire.';
    } else if (champs.remplacantId === contexte.porteurId) {
        erreurs['remplacant'] = 'Le remplaçant doit être différent du commissaire en place.';
    }
    if (!champs.dateDebut) {
        erreurs['dateDebut'] = 'La date de prise d’effet est obligatoire.';
    } else {
        const { min, max } = bornesRemplacementCac(contexte);
        const jour = debutDuJour(champs.dateDebut);
        if (min && jour < min.getTime()) {
            erreurs['dateDebut'] = 'La date de prise d’effet ne peut précéder la désignation du mandat.';
        } else if (max && jour > max.getTime()) {
            erreurs['dateDebut'] = 'La date de prise d’effet ne peut dépasser la fin du mandat.';
        }
    }
    if (!champs.motifId) {
        erreurs['motif'] = 'Le motif de modification est obligatoire.';
    }
    return erreurs;
}

/** `AAAA-MM-JJ` d'une date locale, sans décalage de fuseau. */
function versApi(date: Date): string {
    const mois = String(date.getMonth() + 1).padStart(2, '0');
    const jour = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${mois}-${jour}`;
}

/** Écriture du remplacement : le motif part en libellé, tronqué à la longueur admise par le serveur. */
export function payloadRemplacementCac(mandatId: string, champs: ChampsRemplacementCac, motifs: ParameterValueSummary[]): RemplacementCac {
    const motif = motifs.find((m) => m.id === champs.motifId)?.label ?? '';
    return {
        mandat_id: mandatId,
        role: champs.role,
        personne_physique_remplacant_id: champs.remplacantId,
        motif: motif.slice(0, LONGUEUR_MAX_MOTIF_CAC),
        date_debut_remplacement: champs.dateDebut ? versApi(champs.dateDebut) : undefined,
    };
}
